import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/shared/inputs/button/button";
import { sanitizeHTML } from "@/utility/html.utility";
import styles from "./work-overview.module.scss";

interface WorkOverviewProps {
  title: string;
  html: string;
}

const sanitizeOverview = (html: string): string => sanitizeHTML(html, {
  whiteList: {
    a: ["href", "title"],
    b: [],
    blockquote: [],
    br: [],
    em: [],
    h3: [],
    h4: [],
    i: [],
    li: [],
    ol: [],
    p: [],
    s: [],
    small: [],
    span: [],
    strong: [],
    sub: [],
    sup: [],
    u: [],
    ul: [],
  },
  stripIgnoreTagBody: ["script", "style"],
});

/**
 * Renders the sanitized Works overview and reproduces Legacy's seven-line disclosure.
 * @param root0 Component props.
 * @param root0.title Product-type-specific overview heading supplied by Core.
 * @param root0.html Database-managed overview HTML supplied by Core.
 * @returns Collapsible overview content.
 */
const WorkOverview = ({ title, html }: WorkOverviewProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const sanitizedHtml = useMemo(() => sanitizeOverview(html), [html]);

  useEffect(() => {
    const checkOverflow = () => {
      const content = contentRef.current;
      if (!content) return;
      setIsExpandable(content.scrollHeight > content.clientHeight + 1);
    };

    let animationFrame = window.requestAnimationFrame(checkOverflow);
    const handleResize = () => {
      setIsExpanded(false);
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(checkOverflow);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", handleResize);
    };
  }, [sanitizedHtml]);

  if (!sanitizedHtml) return null;

  return (
    <section className={styles.root} aria-labelledby="work-overview-title">
      <h2 id="work-overview-title" className="u-title-sm">{title}</h2>
      <div
        ref={contentRef}
        className={isExpanded ? styles.content : `${styles.content} ${styles.collapsed}`}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
      {isExpandable && (
        <Button
          className={styles.toggle}
          text={isExpanded ? "Read Less" : "Read Full Overview"}
          variant="link"
          emphasis
          size="inherit"
          onClick={() => setIsExpanded((expanded) => !expanded)}
          ariaLabel={isExpanded ? "Collapse overview" : "Read full overview"}
        />
      )}
    </section>
  );
};

export default WorkOverview;
